import React, { useEffect, useRef, useState } from "react";
import { DumontSidebarProps, ChatMessage, Agent } from "./types";
import { SidebarContainer, BackdropOverlay, AiChatContainer } from "./styles";
import { useConversations } from "./hooks/useConversations";
import { useAvailableAgents } from "./hooks/useAvailableAgents";
import { useDumont } from "../../context/DumontContext";
import { getConvo, getMessages } from "../../api/dumontApi";
import type { Message as DumontMessage } from "../../api/dumontApi";
import SidepanelNavbar from "./components/SidepanelNavbar";
import HeaderControls from "./components/HeaderControls";
import HistoryView from "./components/HistoryView/HistoryView";
import AgentsView from "./components/AgentsView/AgentsView";
import ChatView from "./components/ChatView/ChatView";
import HelpModal from "./components/HelpModal";
import FullscreenSidebar from "./components/FullscreenSidebar";

type ActiveView = "chat" | "history" | "agents";

function DumontSidebarContent({
  isOpen,
  onClose,
  embedded = false,
  onRequestFullPage,
  startFullscreen,
  onExitFullscreenToTab,
}: DumontSidebarProps) {
  const [activeView, setActiveView] = useState<ActiveView>("chat");
  const [isViewingHistoryConversation, setIsViewingHistoryConversation] = useState(false);
  const [isViewingAgentChat, setIsViewingAgentChat] = useState(false);
  const [isViewingDispatchChat, setIsViewingDispatchChat] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [historySearchQuery, setHistorySearchQuery] = useState("");
  const [loadedMessages, setLoadedMessages] = useState<ChatMessage[]>([]);
  const [loadedConversationId, setLoadedConversationId] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const { getConversations } = useConversations();
  const { baseUrl, token, defaultAgentId } = useDumont();
  const { agents: availableAgents, isLoading: isLoadingAgents } = useAvailableAgents();

  const [isFullscreen, setIsFullscreen] = useState(startFullscreen ?? false);

  useEffect(() => {
    if (!selectedAgentId && !isLoadingAgents && availableAgents.length > 0) {
      setSelectedAgentId(defaultAgentId ?? availableAgents[0].id);
    }
  }, [availableAgents, defaultAgentId, selectedAgentId, isLoadingAgents]);

  // ESC to close
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && isFullscreen) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflowY = "hidden";
    } else {
      document.body.style.overflow = "";
      document.documentElement.style.overflowY = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.documentElement.style.overflowY = "";
    };
  }, [isFullscreen, isOpen]);

  const handleNewChat = () => {
    setActiveView("chat");
    setIsViewingHistoryConversation(false);
    setIsViewingAgentChat(false);
    setIsViewingDispatchChat(false);
    setSelectedAgentId(defaultAgentId ?? (isLoadingAgents ? null : availableAgents[0]?.id) ?? null);
    setLoadedMessages([]);
    setLoadedConversationId(null);
  };

  const handleBackToHistory = () => {
    setActiveView("history");
    setIsViewingHistoryConversation(false);
  };

  const handleBackToAgents = () => {
    setActiveView("agents");
    setIsViewingAgentChat(false);
  };

  const handleBackFromDispatch = () => {
    setIsViewingDispatchChat(false);
    setLoadedMessages([]);
    setLoadedConversationId(null);
  };

  function mapDumontMessageToChatMessage(m: DumontMessage): ChatMessage {
    let text = m.text?.trim() ?? "";
    if (!text && Array.isArray(m.content)) {
      text = m.content
        .map((part) => (part && typeof part.text === "string" ? part.text : ""))
        .join("");
    }
    return {
      id: m.messageId ?? `msg-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      text,
      sender: m.isCreatedByUser ? "user" : "dumont",
      timestamp: m.createdAt ? new Date(m.createdAt) : new Date(),
    };
  }

  const handleOpenConversation = async (conversationId: string) => {
    if (!isMountedRef.current) return;
    const conversations = getConversations();
    const localConversation = conversations.find((c) => c.id === conversationId);
    
    if (localConversation && localConversation.messages.length > 0) {
      setLoadedMessages(localConversation.messages);
      setLoadedConversationId(conversationId);
      setActiveView("chat");
      setIsViewingHistoryConversation(true);
      setIsViewingAgentChat(false);
    } else if (baseUrl && token) {
      try {
        await getConvo(baseUrl, token, conversationId);
        if (!isMountedRef.current) return;
        const list = await getMessages(baseUrl, token, conversationId);
        if (!isMountedRef.current) return;
        const messages: ChatMessage[] = (list ?? []).map(mapDumontMessageToChatMessage);
        setLoadedMessages(messages);
        setLoadedConversationId(conversationId);
        setActiveView("chat");
        setIsViewingHistoryConversation(true);
        setIsViewingAgentChat(false);
      } catch (error) {
        console.error('❌ [Dumont] Failed to fetch conversation:', error);
      }
    }
  };

  const handleOpenAgentChat = (agentId: string) => {
    const agent = availableAgents.find((a: Agent) => a.id === agentId);
    if (agent) {
      console.log('🤖 [Dumont] Opening agent chat:', agentId, 'Type:', agent.type);
      setSelectedAgentId(agentId);
      setActiveView("chat");
      setIsViewingAgentChat(true);
      setIsViewingHistoryConversation(false);
      setLoadedMessages([]);
      setLoadedConversationId(null);
    }
  };

  const handleToggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  const handleFullscreenClose = () => {
    if (startFullscreen) {
      onClose();
    } else {
      handleToggleFullscreen();
    }
  };

  const effectiveOpen = isOpen || embedded;
  return (
    <>
     {!embedded && <BackdropOverlay open={isOpen} onClick={onClose} />}
      <SidebarContainer
        className={
          embedded
            ? "embedded"
            : effectiveOpen
            ? isFullscreen
              ? "fullscreen"
              : ""
            : "closed"
        }
      >
        {isFullscreen && (
          <FullscreenSidebar
            onClose={handleFullscreenClose}
            activeView={activeView}
            onHistory={() => setActiveView("history")}
            onNewChat={handleNewChat}
            onAgents={() => setActiveView("agents")}
            onOpenConversation={handleOpenConversation}
            onOpenAgentChat={handleOpenAgentChat}
            onHelpClick={() => setIsHelpModalOpen(true)}
            selectedAgentId={selectedAgentId}
            activeConversationId={loadedConversationId}
          />
        )}

        <SidepanelNavbar
          className={isFullscreen ? "fullscreen" : ""}
          activeView={activeView}
          onNewChat={handleNewChat}
          onHistory={() => setActiveView("history")}
          onAgents={() => setActiveView("agents")}
          onHelpClick={() => setIsHelpModalOpen(true)}
        />

        <AiChatContainer className={isFullscreen ? "fullscreen" : ""}>
          {activeView === "history" ? (
            <HistoryView
              searchQuery={historySearchQuery}
              setSearchQuery={setHistorySearchQuery}
              onOpenConversation={handleOpenConversation}
            />
          ) : activeView === "agents" ? (
            <AgentsView onOpenAgentChat={handleOpenAgentChat} isFullscreen={isFullscreen} selectedAgentId={selectedAgentId} />
          ) : (
            <>
              <HeaderControls
                showBackHistory={isViewingHistoryConversation}
                showBackAgents={isViewingAgentChat}
                showBackDispatch={isViewingDispatchChat}
                onBackHistory={handleBackToHistory}
                onBackAgents={handleBackToAgents}
                onBackDispatch={handleBackFromDispatch}
                onNewChat={handleNewChat}
                onOpenNewWindow={handleToggleFullscreen}
                onRequestFullPage={onRequestFullPage}
                 onExitFullscreenToTab={onExitFullscreenToTab}
                onClose={onClose}
                isFullscreen={isFullscreen}
              />
              <ChatView
                initialMessages={loadedMessages}
                initialConversationId={loadedConversationId}
                isFullscreen={isFullscreen}
                onHistoryClick={() => setActiveView("history")}
                onBackClick={() => setIsViewingDispatchChat(true)}
                showBackButton={isViewingDispatchChat}
                selectedAgentId={selectedAgentId}
              />
            </>
          )}
        </AiChatContainer>
      </SidebarContainer>

      {/* <HelpModal isOpen={isHelpModalOpen} onClose={() => setIsHelpModalOpen(false)} /> */}
    </>
  );
}

export default function DumontSidebar({
  isOpen,
  onClose,
  embedded,
  onRequestFullPage,
  startFullscreen,
  onExitFullscreenToTab,
}: DumontSidebarProps) {
  return (
    <DumontSidebarContent
      isOpen={isOpen}
      onClose={onClose}
      embedded={embedded}
      onRequestFullPage={onRequestFullPage}
      startFullscreen={startFullscreen}
      onExitFullscreenToTab={onExitFullscreenToTab}
    />
  );
}